/**
 * Auth Utility Functions
 *
 * Extracted from AuthContext.tsx for line count compliance (<300 lines).
 * Contains pure/standalone helpers used by AuthProvider.
 */
import { Platform } from "react-native";
import type { AuthError } from "@dramadetector/api-client";
import { BETTER_AUTH_URL } from "../lib/better-auth-client";

/** Construct an AuthError-compatible object without unsafe double-casts. */
export function toAuthError(
	message: string | undefined,
	status = 400,
): AuthError {
	return { message: message ?? "auth.error_generic", status } as AuthError;
}

/**
 * Fetch a JWT from Better Auth's /api/auth/token endpoint.
 * On web, the session cookie is sent automatically (credentials: 'include').
 * On mobile, the session token is sent as a Bearer header.
 *
 * Retries transient failures (network errors, 5xx) with exponential backoff
 * before giving up. A 4xx response (session invalid/revoked) short-circuits
 * retries — no point hammering the server when the cookie itself is dead.
 *
 * Returns null on permanent failure. Callers must NOT downgrade an authenticated
 * session to 'anonymous' on null; instead set status to 'auth_broken' so
 * authenticatedFetch refuses to silently send anonymous-degraded requests.
 * NocoDB #3829 / Sentry DRAMA-WEB-3B.
 */
const JWT_RETRY_BACKOFF_MS = [500, 1500, 4000] as const;

export async function fetchBetterAuthJwt(
	sessionToken: string,
): Promise<string | null> {
	const headers: Record<string, string> = {
		"Content-Type": "application/json",
	};
	if (Platform.OS !== "web") {
		headers["Authorization"] = `Bearer ${sessionToken}`;
	}

	for (let attempt = 0; attempt <= JWT_RETRY_BACKOFF_MS.length; attempt += 1) {
		try {
			const url = `${BETTER_AUTH_URL}/api/auth/token`;
			console.log(
				`[fetchBetterAuthJwt] Attempt ${attempt + 1} to fetch JWT from ${url}`,
			);
			const response = await fetch(url, {
				method: "GET",
				headers,
				credentials: Platform.OS === "web" ? "include" : undefined,
			});
			console.log(`[fetchBetterAuthJwt] Response status: ${response.status}`);
			if (response.ok) {
				const data = await response.json();
				const token = data?.token ?? null;
				console.log(
					`[fetchBetterAuthJwt] SUCCESS: ${token ? "token received" : "no token in response"}`,
				);
				return token;
			}
			// Session truly invalid (revoked / cookie expired). Retrying won't help.
			if (response.status >= 400 && response.status < 500) {
				console.log(
					`[fetchBetterAuthJwt] FAILURE: 4xx response ${response.status} — not retrying`,
				);
				return null;
			}
			// 5xx — fall through to retry
			console.log(
				`[fetchBetterAuthJwt] FAILURE: ${response.status} — will retry`,
			);
		} catch (err) {
			console.log(
				`[fetchBetterAuthJwt] NETWORK ERROR: ${err instanceof Error ? err.message : String(err)}`,
			);
			// Network error — fall through to retry
		}
		const backoff = JWT_RETRY_BACKOFF_MS[attempt];
		if (backoff === undefined) {
			console.log(`[fetchBetterAuthJwt] FAILURE: all retries exhausted`);
			break;
		}
		console.log(`[fetchBetterAuthJwt] Retrying in ${backoff}ms...`);
		await new Promise((r) => setTimeout(r, backoff));
	}
	console.log(
		`[fetchBetterAuthJwt] FAILURE: returning null after all attempts`,
	);
	return null;
}
